import { EFinancingProgram } from '@if/core/types'
import { formatDateTime } from '@if/core/utils'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { Stack, Tooltip } from '@mui/material'
import ActionsDialog from '@src/components/ActionsDialog'
import AsyncActionButton from '@src/components/AsyncActionButton'
import { useSendUploadDocumentRequest } from '@src/data/api/credit-api/upload-document-request-api'
import { Dispatch, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  lastSentOn: Date | null
  setError: Dispatch<Error>
}

const SendUploadRequestButton = ({ creditApplicationId, financingProgramId, lastSentOn, setError }: Props) => {
  const { t } = useTranslation()

  const [sendUploadDocumentRequest, isPending, isError, isSuccess] = useSendUploadDocumentRequest(
    creditApplicationId,
    financingProgramId,
  )

  const [sentOn, setSentOn] = useState<Date | null>(lastSentOn ? new Date(lastSentOn) : null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  useEffect(() => {
    setSentOn(lastSentOn ? new Date(lastSentOn) : null)
  }, [lastSentOn])

  const handleButtonClick = () => {
    setIsDialogOpen(true)
  }

  const handleDialog = useCallback(async () => {
    setIsDialogOpen(false)
    try {
      const result = await sendUploadDocumentRequest()
      setSentOn(result.documentUploadRequestSentOn ? new Date(result.documentUploadRequestSentOn) : null)
    } catch (e) {
      setError(e as Error)
    }
  }, [sendUploadDocumentRequest, setError])

  const handleCancel = useCallback(() => {
    setIsDialogOpen(false)
  }, [])

  return (
    <div>
      <Stack direction="row" alignItems="center" spacing={1}>
        {sentOn && (
          <Tooltip title={t('documentSectionHeaders.lastSentOn', { date: formatDateTime(sentOn) })} placement="top">
            <span>
              <AccessTimeIcon fontSize="small" color="action" />
            </span>
          </Tooltip>
        )}
        <AsyncActionButton
          isPending={isPending}
          isError={isError}
          isSuccess={isSuccess}
          onClick={handleButtonClick}
          fullWidth={false}
          variant="outlined"
        >
          {t('documentSectionHeaders.documentRequest')}
        </AsyncActionButton>
      </Stack>
      <ActionsDialog
        open={isDialogOpen}
        title={t('documentSectionHeaders.sendUploadRequestConfirmTitle')}
        onConfirm={handleDialog}
        onCancel={handleCancel}
      >
        {t('documentSectionHeaders.sendUploadRequestConfirmMessage')}
      </ActionsDialog>
    </div>
  )
}

export default SendUploadRequestButton
